import { State } from '@symbiotic/green-state';

const DEFAULT_DELAY = 5000;
const DISMISS_NEVER = 0;

/**
 * Config parameters passed to "show*" methods below. Message is required, type is
 * optional, other parameters are overrides to the defaults set by each method.
 */
export interface ToastConfig {
  message: string;           // message to be displayed in toast (required)
  type?: string;             // type to be passed later to clearToasts() (optional)
  color?: string;            // reactstrap alert color (see https://reactstrap.github.io/components/alerts) (override)
  iconHint?: string;         // material-ui icon (see https://material-ui.com/components/material-icons) (override)
  autoDismissDelay?: number; // dismiss delay in ms, or 0 for never (override)
}

export interface Toast {
  id: string;
  message: string;
  type?: string;
  color: string;
  iconHint: string;
  autoDismissDelay: number;
  dismiss: () => void;
}

/**
 * ToasterState is a green-state State object representing the array of toast messages
 * currently being displayed by the <Toaster> component. Public methods are exposed
 * for displaying and dismissing toast messages.
 */
export class ToasterState extends State<{ toasts: Toast[] }> {

  constructor() {
    super({ toasts: [] });
  }

  /**
   * Display an generic toast message. By default, it renders no icon
   * on a "dark" background, with an auto-dismiss delay of 5 seconds.
   */
  showToast = (config: ToastConfig) => {
    const toast: Toast = {
      ...config,
      id: String(Date.now()),
      color: config.color || 'dark',
      iconHint: config.iconHint || '',
      autoDismissDelay: config.autoDismissDelay === DISMISS_NEVER ? DISMISS_NEVER : (config.autoDismissDelay || DEFAULT_DELAY),
      dismiss: () => this.setState({ toasts: this.state.toasts.filter(t => t !== toast) })
    };

    this.setState({ toasts: this.state.toasts.concat([toast]) });

    if (toast.autoDismissDelay) {
      setTimeout(() => toast.dismiss(), toast.autoDismissDelay);
    }
  }

  /**
   * Display an info toast message. By default, it renders an "i" icon
   * on a "primary" background, with an auto-dismiss delay of 5 seconds.
   */
  showInfo = (config: ToastConfig) => {
    this.showToast({
      ...config,
      color: config.color || 'primary',
      iconHint: config.iconHint || 'info_outline'
    });
  }

  /**
   * Display a success toast message. By default, it renders a "check" icon
   * on a "success" background, with an auto-dismiss delay of 5 seconds.
   */
  showSuccess = (config: ToastConfig) => {
    this.showToast({
      ...config,
      color: config.color || 'success',
      iconHint: config.iconHint || 'check_circle_outline'
    });
  }

  /**
   * Display a warning toast message. By default, it renders an "!" icon
   * on a "warning" background, with an auto-dismiss delay of 5 seconds.
   */
  showWarning = (config: ToastConfig) => {
    this.showToast({
      ...config,
      color: config.color || 'warning',
      iconHint: config.iconHint || 'error_outline'
    });
  }

  /**
   * Display an error toast message. By default, it renders an "x"
   * icon on a "danger" background, and does not auto-dismiss.
   */
  showError = (config: ToastConfig) => {
    this.showToast({
      ...config,
      color: config.color || 'danger',
      iconHint: config.iconHint || 'highlight_off',
      autoDismissDelay: config.autoDismissDelay || DISMISS_NEVER
    });
  }

  /**
   * Clear displayed toast messages before they have been dismissed.
   * Intended to be used before displaying a new message of the same type.
   * Requires that a "type" parameter was provided when the toast was displayed.
   */
  clearToasts = ({ types }: { types: Array<string> }) => {
    const matchesTypes = (toast: Toast) => types.find((type) => type === toast.type);
    for (const toast of this.get().toasts.filter(matchesTypes)) {
      this.setState({ toasts: this.state.toasts.filter(t => t !== toast) });
    }
  }
}



// WEBPACK FOOTER //
// ./src/framework/ToasterState.ts